import { PENDING_ORDER_IDEMPOTENCY } from "../common/constants";

// Tracks the Create Order idempotency key against a fingerprint of the bill it was
// generated for, so a retry with an unchanged bill (e.g. a failed/offline payment)
// reuses the same key instead of minting a new one on every attempt. "Unchanged"
// means items AND every other bill-affecting field (tip, delivery address, offers,
// order type, grand total) — not just the item list, otherwise editing the tip (or
// anything else that isn't a line item) after a failed payment would silently reuse
// a stale order instead of creating a fresh one with the new total.
interface PendingOrderRecord {
  idempotencyKey: string;
  billFingerprint: string;
  customerId: string;
  orderId?: string;
  orderData?: any;
}

const computeBillFingerprint = (orderPayload: any): string =>
  JSON.stringify({
    items: (orderPayload?.items || []).map((item: any) => [
      item?.itemId,
      item?.quantity,
      item?.subTotal,
      item?.modifierIds,
    ]),
    tip: orderPayload?.tip,
    tipType: orderPayload?.tipType,
    addressId: orderPayload?.addressId,
    orderTypeId: orderPayload?.orderTypeId,
    offerCaptureRequests: orderPayload?.offerCaptureRequests,
    couponCode: orderPayload?.couponCode,
    // Catch-all: any other bill-affecting change (delivery fee, discounts, etc.)
    // ultimately shows up here even if it isn't one of the fields listed above.
    orderTotal: orderPayload?.orderTotal,
  });

const readPendingOrder = (): PendingOrderRecord | null => {
  try {
    const raw = localStorage.getItem(PENDING_ORDER_IDEMPOTENCY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writePendingOrder = (record: PendingOrderRecord): void => {
  try {
    localStorage.setItem(PENDING_ORDER_IDEMPOTENCY, JSON.stringify(record));
  } catch {}
};

const generateIdempotencyKey = (customerId: string): string => {
  const uniqueId = crypto.randomUUID();
  return `${Date.now()}-${uniqueId}-${customerId || "guest"}`;
};

// Resolves the idempotency key to send with the next Create Order request for this
// bill (the full order payload — items, tip, delivery address, offers, order type,
// total). If a prior attempt already created an order for this exact bill (same
// customer + same fingerprint), that order is returned as `reusableOrder` so the
// caller can skip calling Create Order again — e.g. retrying after a failed/offline
// payment. Any change to the bill (or customer) mints a fresh key and drops the stale
// order reference, since it no longer represents what's being ordered.
export const resolveOrderIdempotency = (
  orderPayload: any,
  customerId: string
): { idempotencyKey: string; reusableOrder: { orderId: string; orderData: any } | null } => {
  const billFingerprint = computeBillFingerprint(orderPayload);
  const normalizedCustomerId = customerId || "";
  const pending = readPendingOrder();

  if (pending && pending.customerId === normalizedCustomerId && pending.billFingerprint === billFingerprint) {
    if (pending.orderId && pending.orderData) {
      return {
        idempotencyKey: pending.idempotencyKey,
        reusableOrder: { orderId: pending.orderId, orderData: pending.orderData },
      };
    }
    // Same bill, but the previous Create Order attempt never confirmed success —
    // retry with the same key so the backend can dedupe if it actually went through.
    return { idempotencyKey: pending.idempotencyKey, reusableOrder: null };
  }

  const idempotencyKey = generateIdempotencyKey(normalizedCustomerId);
  writePendingOrder({ idempotencyKey, billFingerprint, customerId: normalizedCustomerId });
  return { idempotencyKey, reusableOrder: null };
};

// Records that Create Order succeeded for the currently-pending idempotency key, so a
// later retry (payment failed/offline) can reuse the resulting order instead of
// calling Create Order again.
export const markOrderCreated = (idempotencyKey: string, orderId: string, orderData: any): void => {
  const pending = readPendingOrder();
  if (!pending || pending.idempotencyKey !== idempotencyKey) return;
  writePendingOrder({ ...pending, orderId, orderData });
};

// Called once the order is fully completed (payment succeeded), so the next order
// starts with a clean slate.
export const clearPendingOrderIdempotency = (): void => {
  try {
    localStorage.removeItem(PENDING_ORDER_IDEMPOTENCY);
  } catch {}
};
